import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useAssistantContext } from "../../contexts/AssistantContext";
import { stepQuestions } from "../../data/assistantPrompts";
import { readSseStream } from "../../utils/sse";
import { log } from "../../utils/logger";
import AssistantPanel from "./AssistantPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ALLOWED_PATHS = ["/ebook", "/quickstart", "/faq"];

// Session id is a random UUID (no PII) so chat history can be resumed across visits.
const sessionKey = (userId) => `assistant_session_${userId}`;
const getSession = (userId) => {
  let id = localStorage.getItem(sessionKey(userId));
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(sessionKey(userId), id);
  }
  return id;
};

const withId = (m) => ({ ...m, id: m.id || m.created_at || crypto.randomUUID() });

const appendDelta = (delta) => (prev) => {
  const next = [...prev];
  const last = next[next.length - 1];
  next[next.length - 1] = { ...last, content: last.content + delta };
  return next;
};

async function postChat({ message, sessionId, context, signal }) {
  const res = await fetch(`${API}/assistant/chat`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ message, session_id: sessionId, context }),
    signal,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.detail || `Request failed (${res.status})`);
  }
  return res;
}

export default function AssistantWidget() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const { context } = useAssistantContext();
  const contextLabel = context?.label || null;
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [meta, setMeta] = useState({ suggested_questions: [], daily_limit: 0, used_today: 0 });
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState("");
  const [sessionId, setSessionId] = useState("");
  const abortRef = useRef(null);

  const visible = !!user && ALLOWED_PATHS.includes(pathname);

  const loadSession = useCallback(async (sid) => {
    try {
      const [{ data: hist }, { data: m }] = await Promise.all([
        axios.get(`${API}/assistant/history`, { params: { session_id: sid }, withCredentials: true }),
        axios.get(`${API}/assistant/meta`, { withCredentials: true }),
      ]);
      setMessages((hist.messages || []).map(withId));
      setMeta(m);
    } catch (e) {
      log.warn("assistant load failed", e);
    }
  }, []);

  useEffect(() => {
    if (!user || !open) return;
    const sid = getSession(user.id);
    setSessionId(sid);
    loadSession(sid);
  }, [user, open, loadSession]);

  const send = useCallback(
    async (text) => {
      const trimmed = text.trim();
      if (!trimmed || streaming) return;
      setError("");
      setMessages((prev) => [...prev, withId({ role: "user", content: trimmed }), withId({ role: "assistant", content: "" })]);
      setStreaming(true);
      const controller = new AbortController();
      abortRef.current = controller;
      try {
        const res = await postChat({ message: trimmed, sessionId, context: contextLabel, signal: controller.signal });
        await readSseStream(res, (payload) => {
          if (payload.delta) setMessages(appendDelta(payload.delta));
          else if (payload.error) throw new Error(payload.error);
        });
        setMeta((m) => ({ ...m, used_today: m.used_today + 1 }));
      } catch (e) {
        if (e.name !== "AbortError") {
          setError(e.message || "Something went wrong");
          setMessages((prev) => (prev[prev.length - 1]?.content === "" ? prev.slice(0, -1) : prev));
        }
      } finally {
        setStreaming(false);
        abortRef.current = null;
      }
    },
    [sessionId, streaming, contextLabel]
  );

  const newChat = useCallback(() => {
    if (!user) return;
    abortRef.current?.abort();
    const sid = crypto.randomUUID();
    localStorage.setItem(sessionKey(user.id), sid);
    setSessionId(sid);
    setMessages([]);
    setError("");
  }, [user]);

  if (!visible) return null;

  return (
    <AssistantPanel
      open={open}
      onToggle={() => setOpen((o) => !o)}
      messages={messages}
      meta={meta}
      streaming={streaming}
      error={error}
      onSend={send}
      onNewChat={newChat}
      isPremium={user.is_premium || user.role === "admin"}
      stepContext={context ? { label: context.label, questions: stepQuestions[context.key] || [] } : null}
      raised={pathname === "/ebook"}
    />
  );
}
